<template>
  <section class="resource-section">
    <!-- 書籍配備 -->
    <div v-if="equipmentGroups.length" class="resource-block">
      <div class="section-heading">
        <span class="eyebrow">MATERIALS</span>
        <h2>{{ materialsTitle }}</h2>
      </div>

      <div class="equipment-group-list">
        <section
          v-for="(group, groupIndex) in equipmentGroups"
          :key="`${group.title || 'equipment'}-${groupIndex}`"
          class="equipment-group"
        >
          <!-- Google Sheet 的【群組標題】 -->
          <div
            v-if="group.title"
            class="equipment-group-heading"
          >
            <h3>{{ group.title }}</h3>
          </div>

          <!-- 每一個 • 都是一張小卡 -->
          <div class="equipment-grid">
            <article
              v-for="(item, itemIndex) in group.cards"
              :key="`${item.title}-${groupIndex}-${itemIndex}`"
              class="equipment-card"
            >
              <span class="equipment-no">
                {{
                  String(
                    group.startIndex + itemIndex + 1
                  ).padStart(2, "0")
                }}
              </span>

              <!-- 有圖片 -->
              <button
                v-if="item.image"
                type="button"
                class="equipment-image-button"
                :aria-label="`${item.title}－點擊放大圖片`"
                @click="openLightbox(item.image, item.title)"
              >
                <img
                  :src="item.image"
                  :alt="item.title"
                  loading="lazy"
                />
              </button>

              <!-- 暫時沒有圖片 -->
              <div
                v-else
                class="equipment-icon"
                aria-hidden="true"
              >
                ▤
              </div>

              <h3>{{ item.title }}</h3>
            </article>
          </div>
        </section>
      </div>
    </div>

    <!-- 配備圖片 -->
    <div v-if="imageList.length" class="resource-block">
      <div class="section-heading compact">
        <span class="eyebrow">OVERVIEW</span>
        <h2>{{ imageTitle }}</h2>
      </div>

      <div class="resource-image-grid">
        <button
          v-for="(image, index) in imageList"
          :key="`${image}-${index}`"
          type="button"
          class="resource-image"
          :aria-label="`${imageAlt} ${index + 1}－點擊放大圖片`"
          @click="openLightbox(image, `${imageAlt} ${index + 1}`)"
        >
          <img
            :src="image"
            :alt="`${imageAlt} ${index + 1}`"
            loading="lazy"
          />
        </button>
      </div>
    </div>

    <!-- 下載資源與相關教材 -->
    <div v-if="filteredLinks.length" class="resource-block">
      <div class="section-heading compact">
        <span class="eyebrow">RESOURCES</span>
        <h2>{{ downloadsTitle }}</h2>
      </div>

      <div class="download-grid">
        <a
          v-for="(link, index) in filteredLinks"
          :key="`${link.href}-${index}`"
          class="download-card"
          :href="link.href"
          target="_blank"
          rel="noopener noreferrer"
        >
          <span
            class="download-icon"
            :class="{ 'has-image': link.image }"
            aria-hidden="true"
          >
            <img
              v-if="link.image"
              :src="link.image"
              alt=""
              loading="lazy"
            />
            <span v-else>↗</span>
          </span>

          <span class="download-copy">
            <small>{{ link.type || "RESOURCE" }}</small>
            <strong>{{ link.label }}</strong>
          </span>

          <span class="download-arrow" aria-hidden="true">
            →
          </span>
        </a>
      </div>
    </div>

    <!-- 完全沒有資料 -->
    <div
      v-if="
        !equipmentGroups.length &&
        !imageList.length &&
        !filteredLinks.length
      "
      class="empty-state"
    >
      {{ emptyText }}
    </div>

    <!-- 圖片燈箱 -->
    <Teleport to="body">
      <Transition name="lightbox-fade">
        <div
          v-if="lightboxImage"
          class="image-lightbox"
          role="dialog"
          aria-modal="true"
          :aria-label="lightboxAlt"
          @click.self="closeLightbox"
        >
      <div class="image-lightbox-panel">
          <button
            type="button"
            class="lightbox-close"
            aria-label="關閉圖片"
            @click="closeLightbox"
          >
            ×
          </button>

          <img
            :src="lightboxImage"
            :alt="lightboxAlt"
            class="image-lightbox-content"
          />
        
      </div>
    </div>
      </Transition>
    </Teleport>
  </section>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";

export default {
  name: "ResourceSection",

  props: {
    provide: {
      type: String,
      default: "",
    },

    images: {
      type: Array,
      default: () => [],
    },

    imageAlt: {
      type: String,
      default: "",
    },

    materialsTitle: {
      type: String,
      default: "教材配備",
    },

    imageTitle: {
      type: String,
      default: "配備圖片",
    },

    downloadsTitle: {
      type: String,
      default: "下載資源與相關教材",
    },

    emptyText: {
      type: String,
      default: "目前尚無書籍配備資料。",
    },

    links: {
      type: Array,
      default: () => [],
    },
  },

  setup(props) {
    const lightboxImage = ref("");
    const lightboxAlt = ref("");

    const openLightbox = (image, alt = "") => {
      lightboxImage.value = image;
      lightboxAlt.value = alt;
      document.body.style.overflow = "hidden";
    };

    const closeLightbox = () => {
      lightboxImage.value = "";
      lightboxAlt.value = "";
      document.body.style.overflow = "";
    };

    const handleKeydown = (event) => {
      if (event.key === "Escape" && lightboxImage.value) closeLightbox();
    };

    onMounted(() => window.addEventListener("keydown", handleKeydown));
    onBeforeUnmount(() => {
      window.removeEventListener("keydown", handleKeydown);
      document.body.style.overflow = "";
    });

    const resolveImageUrl = (imageUrl) => {
      const value = String(imageUrl || "").trim();

      if (!value) return "";

      if (/^(https?:)?\/\//i.test(value) || value.startsWith("data:")) {
        return value;
      }

      return `${process.env.BASE_URL}${value.replace(/^\//, "")}`;
    };

const equipmentGroups = computed(() => {
  const text = String(props.provide || "")
    .replace(/\r\n?/g, "\n")
    .trim();

  if (!text) return [];

  const lines = text
    .split("\n")
    .map((line) => line.trim());

  const groups = [];

  let currentGroup = {
    title: "",
    cards: [],
    startIndex: 0,
  };

  let currentImage = "";

  lines.forEach((line) => {
    if (!line) return;

    // ===== 群組 =====
    const groupMatch = line.match(/^【(.+?)】$/);

    if (groupMatch) {
      if (currentGroup.cards.length) {
        groups.push(currentGroup);
      }

      currentGroup = {
        title: groupMatch[1].trim(),
        cards: [],
        startIndex: 0,
      };

      currentImage = "";
      return;
    }

    // ===== 圖片 =====
    const imageMatch = line.match(
      /^(?:圖片|Image)\s*[：:]\s*(.*)$/i
    );

    if (imageMatch) {
      currentImage = resolveImageUrl(
        imageMatch[1] || ""
      );

      return;
    }

    // ===== 卡片 =====
    const titleMatch = line.match(
      /^[•●▪◦‧·✓✔]\s*(.+)$/
    );

    if (titleMatch) {
      currentGroup.cards.push({
        image: currentImage,
        title: titleMatch[1].trim(),
      });

      // 一張圖片只屬於一張卡
      currentImage = "";

      return;
    }
  });

  if (currentGroup.cards.length) {
    groups.push(currentGroup);
  }

  // 重新編號
  let count = 0;

  groups.forEach((group) => {
    group.startIndex = count;
    count += group.cards.length;
  });

  return groups;
});

    const imageList = computed(() =>
      props.images.map((image) => String(image || "").trim()).filter(Boolean),
    );

    const filteredLinks = computed(() =>
      props.links
        .filter((item) => item?.href && item?.label)
        .map((item) => ({
          ...item,
          href: String(item.href).trim(),
          label: String(item.label).trim(),
          type: item.type ? String(item.type).trim() : "RESOURCE",
        })),
    );

    return {
      equipmentGroups,
      imageList,
      filteredLinks,
      lightboxImage,
      lightboxAlt,
      openLightbox,
      closeLightbox,
    };
  },
};
</script>

<style scoped>
.resource-section {
  display: grid;
  gap: 46px;
  padding: 40px 2px 12px;
}

.resource-block {
  display: grid;
  gap: 22px;
}

.section-heading {
  text-align: center;
}

.eyebrow {
  display: block;
  margin-bottom: 6px;
  color: #24618d;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.16em;
}

.section-heading h2 {
  margin: 0;
  color: #19364d;
  font-size: clamp(24px, 2.5vw, 32px);
  font-weight: 750;
}

.equipment-group-list {
  display: flex;
  flex-direction: column;
  gap: 42px;
}

.equipment-group-heading {
  display: flex;
  align-items: center;
  gap: 18px;
  margin-bottom: 18px;
}

.equipment-group-heading::after {
  content: "";
  flex: 1;
  height: 1px;
  background: #d9e5ec;
}

.equipment-group-heading h3 {
  margin: 0;
  color: #19364d;
  font-size: 22px;
  font-weight: 700;
}

.equipment-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.equipment-card {
  position: relative;
  display: grid;
  grid-template-columns: 110px minmax(0, 1fr);
  align-items: center;
  gap: 20px;
  min-height: 150px;
  padding: 24px 48px 24px 24px;
  overflow: hidden;
  border: 1px solid #e0e8ee;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 10px 28px rgba(31, 55, 73, 0.06);
  text-align: left;
}

.equipment-image-button {
  position: relative;
  z-index: 1;
  display: grid;
  width: 90px;
  height: 100px;
  padding: 6px;
  overflow: hidden;
  place-items: center;
  border: 0;
  border-radius: 12px;
  background: #f6f8fa;
  cursor: zoom-in;
  appearance: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.equipment-image-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 24px rgba(31, 55, 73, 0.12);
}

.equipment-image-button img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.equipment-icon {
  display: grid;
  place-items: center;
  width: 90px;
  height: 100px;
  border-radius: 12px;
  background: #edf5f8;
  color: #7d99ac;
  font-size: 32px;
  font-weight: bold;
}

.equipment-card h3 {
  position: relative;
  z-index: 1;
  margin: 0;
  color: #2b414f;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.55;
  text-align: left;
}

.equipment-card::after {
  content: "";
  position: absolute;
  right: -30px;
  bottom: -40px;
  width: 110px;
  height: 110px;
  border-radius: 50%;
  background: #edf5f8;
}

.equipment-no {
  position: absolute;
  top: 17px;
  right: 19px;
  color: #a8b8c2;
  font-size: 13px;
  font-weight: 700;
}

.equipment-icon img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.equipment-icon span {
  color: #24618d;
  font-size: 24px;
}

.equipment-card h3 {
  position: relative;
  z-index: 1;
  margin: 0;
  color: #2b414f;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.55;
}

.resource-image-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.resource-image {
  margin: 0;
  overflow: hidden;
  border: 1px solid #e1e8ed;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 14px 34px rgba(31, 55, 73, 0.06);
}

.resource-image img {
  display: block;
  width: 100%;
  height: auto;
}

.download-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.download-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px;
  border: 1px solid #dfe7ed;
  border-radius: 16px;
  background: #fff;
  color: inherit;
  text-decoration: none;
  transition: 0.2s ease;
}

.download-card:hover {
  transform: translateY(-2px);
  border-color: #9db9cc;
  box-shadow: 0 12px 28px rgba(31, 55, 73, 0.08);
}

.download-icon {
  display: grid;
  place-items: center;
  width: 42px;
  height: 42px;
  flex: 0 0 auto;
  border-radius: 12px;
  background: #eaf3f8;
  color: #24618d;
  font-size: 20px;
  overflow: hidden;
}

.download-icon.has-image {
  width: 58px;
  height: 72px;
  border-radius: 8px;
  background: #fff;
}

.download-icon img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.download-copy {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.download-copy small {
  color: #8a98a3;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.12em;
}

.download-copy strong {
  color: #2d4454;
  font-size: 15px;
  line-height: 1.5;
  overflow-wrap: anywhere;
}

.download-arrow {
  color: #24618d;
  font-size: 22px;
}

.empty-state {
  padding: 44px 20px;
  color: #7b8792;
  text-align: center;
}

.image-lightbox {
  position: fixed;
  z-index: 99999;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 28px;
  background: rgba(0, 0, 0, 0.78);
}

.lightbox-close {
  position: absolute;
  z-index: 100000;
  top: -18px;
  right: -18px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  padding: 0;
  border: 2px solid rgba(23, 79, 125, 0.12);
  border-radius: 50%;
  background: #fff;
  color: #174f7d;
  font-family: Arial, sans-serif;
  font-size: 30px;
  font-weight: 400;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.32);
}

.lightbox-close:hover {
  background: #f1f6f9;
  transform: scale(1.05);
}

.lightbox-close:focus-visible {
  outline: 3px solid rgba(255, 255, 255, 0.9);
  outline-offset: 3px;
}

.lightbox-fade-enter-active,
.lightbox-fade-leave-active {
  transition: opacity 0.2s ease;
}

.lightbox-fade-enter-from,
.lightbox-fade-leave-to {
  opacity: 0;
}

@media (max-width: 900px) {
  .equipment-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 600px) {
  .resource-section {
    padding-top: 30px;
  }

  .equipment-grid,
  .download-grid,
  .resource-image-grid {
    grid-template-columns: 1fr;
  }

    .equipment-card {
    grid-template-columns: 76px minmax(0, 1fr);
    gap: 16px;
    min-height: 130px;
    padding: 20px 44px 20px 20px;
  }

  .equipment-image-button,
  .equipment-icon {
    width: 76px;
    height: 88px;
  }

  .equipment-group-heading h3{
        font-size:18px;

    }
}


.image-lightbox-panel {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  max-width: 92vw;
  max-height: 88vh;
}


.image-lightbox-content {
  display: block;
  width: auto;
  max-width: 92vw;
  max-height: 88vh;
  height: auto;
  object-fit: contain;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 18px 55px rgba(0, 0, 0, 0.35);
}
</style>
